import { SERVER_URL } from 'src/constants/serverUrl'
import { useMutation, UseMutationOption } from 'src/hooks/use-mutation'
import {
  GqlError,
  UploadFilesMutation,
  UploadFilesMutationVariables,
  UploadTrialHospitalSharedFilesMutation,
  UploadTrialHospitalSharedFilesMutationVariables,
} from 'src/lib/gql-client'
import { isNotNullish } from 'src/utils/isNotNullish'

import uploadFileQuery from './mutation.gql?raw' // graphqlファイルを文字列として読み込み
import uploadTrialHospitalSharedFileQuery from './uploadTrialHospitalSharedFiles.gql?raw'

type UploadFilesRes = UploadFilesMutation['uploadFiles']

type UploadTrialHospitalSharedFilesRes =
  UploadTrialHospitalSharedFilesMutation['uploadAndRegisterTrialHospitalSharedFiles']

export type UploadedFiles = UploadFilesRes['uploadedFiles']

type UploadRes = UploadFilesRes | UploadTrialHospitalSharedFilesRes

type UploadMutation =
  UploadFilesMutation | UploadTrialHospitalSharedFilesMutation

type MutationVariables =
  UploadFilesMutationVariables | UploadTrialHospitalSharedFilesMutationVariables

export type UploadFilesOptions = {
  trialHospitalUid: string
  registerFilesOrder: string[]
}

type UploadFilesInput = {
  files: File[]
}

type Operations = {
  query: string
  variables: MutationVariables
  operationName: string
}

type RequestResponse = {
  data: UploadMutation | null
  errors: GqlError[] | undefined | null
}

const isUploadFilesMutation = (data: unknown): data is UploadFilesMutation => {
  return isNotNullish(data) && 'uploadFiles' in (data as Object)
}

const isUploadTrialHospitalSharedFilesMutation = (
  data: unknown,
): data is UploadTrialHospitalSharedFilesMutation => {
  return (
    isNotNullish(data) &&
    'uploadAndRegisterTrialHospitalSharedFiles' in (data as Object)
  )
}

// https://github.com/jaydenseric/graphql-multipart-request-spec?tab=readme-ov-file#multipart-form-field-structure で示されているようにFormDataを構築する
// JSON形式ででoperations, map, マッピング対象のファイルの指定を行いFormDataに設定する
const createUploadFiles = <T extends UploadRes>(
  uploadFileOptions?: UploadFilesOptions,
) => {
  return async ({ files }: UploadFilesInput): Promise<T> => {
    const operations: Operations = uploadFileOptions
      ? {
          query: uploadTrialHospitalSharedFileQuery,
          variables: {
            input: {
              files: files.map(_ => null),
              trialHospitalUid: uploadFileOptions.trialHospitalUid,
              registerFilesOrder: uploadFileOptions.registerFilesOrder,
            },
          }, // アップロードするファイル分のサイズを持った配列を作成
          operationName: 'UploadTrialHospitalSharedFiles',
        }
      : {
          query: uploadFileQuery,
          variables: { input: { files: files.map(_ => null) } }, // アップロードするファイル分のサイズを持った配列を作成
          operationName: 'UploadFiles',
        }
    const map: { [key: number]: string[] } = {}
    files.forEach((_, i) => {
      map[i] = [`variables.input.files.${i}`]
    })

    // FormDataの構築
    const formData = new FormData()
    formData.append('operations', JSON.stringify(operations))
    formData.append('map', JSON.stringify(map))
    // fileをappend(keyはmapで指定している通りindex)
    files.forEach((file, i) => {
      formData.append(String(i), file)
    })

    const fetchRes = await fetch(`${SERVER_URL}/query`, {
      method: 'post',
      body: formData,
      credentials: 'include',
    })
    // request自体が失敗している場合はnullを指定してデフォルトのエラーメッセージ（問題が発生しました...）が表示されるようにする。
    if (!fetchRes.ok) {
      throw new GqlError()
    }
    const res: RequestResponse = await fetchRes.json()
    // GraphQLのエラーはレスポンスに配列で含まれるので最初のエラーを返す
    if (isNotNullish(res.errors) && res.errors.length > 0) {
      const firstError = res.errors[0]
      throw firstError
    }
    if (res.data !== null) {
      if (isUploadFilesMutation(res.data)) {
        return res.data.uploadFiles as T
      } else if (isUploadTrialHospitalSharedFilesMutation(res.data)) {
        return res.data.uploadAndRegisterTrialHospitalSharedFiles as T
      }
    }

    console.error('Unexpected response', res)
    // 最終的に値をreturnできなかった場合 こちらもデフォルトのエラーを返す
    throw new GqlError()
  }
}

type UseUploadFilesOptions = {
  mutationOptions?: UseMutationOption<UploadRes, GqlError>
  uploadFilesOptions?: UploadFilesOptions
}
export const useUploadFiles = <T extends UploadRes>({
  mutationOptions,
  uploadFilesOptions,
}: UseUploadFilesOptions) => {
  const uploadFiles = createUploadFiles<T>(uploadFilesOptions)
  return useMutation(uploadFiles, mutationOptions)
}
